<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Évolution de la conso",
            button_rapport: "Rapport",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const buildingStore = useBuildingStore()
const emConsoCompareStore = useEmConsoCompareStore()

const { isFetchingBuilding, isFetchingBuildingSources } =
    storeToRefs(buildingStore)

// ================================
// CHARGEMENT
// ================================
/**
 * Hydrate le scope comparaison une fois le bâtiment aligné sur la route et le chargement
 * (bâtiment + sources) terminé — le fetch est assuré par {@link PageBuildingWrapper}.
 * La clé localStorage `accueil-em-preferences` est partagée avec le dashboard org (décision produit).
 */
const getIsEvolutionContextLoading = computed(
    () => isFetchingBuilding.value || isFetchingBuildingSources.value
)

watch(
    () => ({
        id: route.params?._id_building as string | undefined,
        b: buildingStore.building,
        loading: getIsEvolutionContextLoading.value,
    }),
    ({ id, b, loading }) => {
        if (!id || loading || !b) return
        const bid = b._id_building ?? b._id
        if (bid !== id) return
        emConsoCompareStore.hydrateCompareScopeForBuilding(b)
    },
    { immediate: true }
)

// ================================
// DONNÉES CALCULÉES
// ================================
const getBuildingId = computed(
    () => route.params?._id_building as string | undefined
)

/**
 * Prêt à monter la carte de comparaison : chargement terminé et scope hydraté pour la route.
 */
const getIsCompareReady = computed(() => {
    const routeId = getBuildingId.value
    if (!routeId || getIsEvolutionContextLoading.value) return false
    const b = buildingStore.building
    if (!b) return false
    const bid = b._id_building ?? b._id
    if (bid !== routeId) return false
    return emConsoCompareStore.compareListId.length > 0
})

// ================================
// INTERACTIONS
// ================================
// /**
//  * Export / rapport : réservé à un futur service métier ($createRapport).
//  */
// function onClickRapport(): void {
//     // TODO: brancher le service de récupération de rapport
// }
</script>

<template>
    <div class="page page-energy-manager-evolution-conso">
        <HeaderPageBuilding
            :title="t('title')"
            class-header="flex flex-col gap-4 items-start sm:flex-row sm:items-center sm:justify-between"
        >
            <!-- <BaseButton
                :label="t('button_rapport')"
                severity="primary"
                @click="onClickRapport"
            /> -->
        </HeaderPageBuilding>

        <template v-if="getIsEvolutionContextLoading">
            <BaseSkeleton width="100%" height="46rem" />
        </template>

        <DashboardCardCompare v-else-if="getIsCompareReady" />

        <BaseMessage
            v-else-if="buildingStore.hasErrorBuilding"
            icon="pi pi-exclamation-triangle"
            severity="error"
            class="w-fit"
        >
            {{ buildingStore.errorBuilding?.message }}
        </BaseMessage>
    </div>
</template>
